import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import type { Invitation } from '@chore-project/shared';

export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) return;
    apiClient
      .get<Invitation>(`/invitations/${token}`)
      .then((res) => setInvitation(res.data))
      .catch(() => setError('Invitation not found or expired.'));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      await apiClient.post(`/invitations/${token}/accept`);
      navigate('/');
    } catch {
      setError('Failed to accept invitation. It may have already been accepted or expired.');
    } finally {
      setAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">Chore Project</div>
        <h1 className="auth-title">Household Invitation</h1>

        {error && (
          <div className="alert alert-error" style={{ marginTop: '1rem' }}>
            {error}
          </div>
        )}

        {invitation && !error && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginBottom: '1.5rem' }}>
              You&apos;ve been invited to join a household.
            </p>
            {!isAuthenticated ? (
              <div>
                <p
                  style={{
                    marginBottom: '1rem',
                    color: 'var(--text-muted)',
                    fontSize: '0.9rem',
                    textAlign: 'center',
                  }}
                >
                  Sign in or create an account to accept this invitation.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Link
                    to={`/signin?redirect=/invitations/${token}`}
                    className="btn btn-primary btn-full"
                  >
                    Sign In
                  </Link>
                  <Link to="/signup" className="btn btn-ghost btn-full">
                    Sign Up
                  </Link>
                </div>
              </div>
            ) : (
              <button
                onClick={() => void handleAccept()}
                disabled={accepting}
                className="btn btn-primary btn-full"
              >
                {accepting ? 'Joining…' : 'Accept & Join Household'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
